import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  CommunicationIntegration,
  EmailIntegrationConfig,
} from "@/types/communication-integration";
import {
  createEmailIntegrationSchema,
  EmailIntegrationFormData,
} from "../utils/integration-schemas";
import { buildEmailIntegrationPayload } from "../utils/payload-builders";
import { useUpsertEmailIntegration } from "../hooks/communication-integrations.hooks";
import { SecretField } from "./SecretField";

interface EmailIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hotelId: string;
  existing: CommunicationIntegration | null;
}

export const EmailIntegrationDialog: React.FC<EmailIntegrationDialogProps> = ({
  open,
  onOpenChange,
  hotelId,
  existing,
}) => {
  const { t } = useTranslation();
  const emailConfig =
    existing?.config.kind === "EMAIL" ? existing.config : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existing
              ? t("communicationChannels.email.editTitle")
              : t("communicationChannels.email.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("communicationChannels.email.dialogDescription")}
          </DialogDescription>
        </DialogHeader>
        {open && (
          <EmailIntegrationForm
            hotelId={hotelId}
            existing={existing}
            existingEmailConfig={emailConfig}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface EmailIntegrationFormProps {
  hotelId: string;
  existing: CommunicationIntegration | null;
  existingEmailConfig: EmailIntegrationConfig | null;
  onClose: () => void;
}

const EmailIntegrationForm: React.FC<EmailIntegrationFormProps> = ({
  hotelId,
  existing,
  existingEmailConfig,
  onClose,
}) => {
  const { t } = useTranslation();
  const isFirstSetup = existing === null;
  const isCurrentlyEnabled = existing?.enabled ?? false;
  const canClearSecrets = !!existing && !isCurrentlyEnabled;
  const { mutate: upsert, isPending } = useUpsertEmailIntegration(hotelId);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EmailIntegrationFormData>({
    resolver: zodResolver(
      createEmailIntegrationSchema({ isFirstSetup, isCurrentlyEnabled }),
    ),
    defaultValues: {
      enabled: isCurrentlyEnabled,
      smtp: {
        host: existingEmailConfig?.smtp.host ?? "",
        port: existingEmailConfig?.smtp.port ?? 587,
        secure: existingEmailConfig?.smtp.secure ?? false,
        username: existingEmailConfig?.smtp.username ?? "",
        password: "",
        clearPassword: false,
        fromAddress: existingEmailConfig?.smtp.fromAddress ?? "",
        fromName: existingEmailConfig?.smtp.fromName ?? "",
        replyToAddress: existingEmailConfig?.smtp.replyToAddress ?? "",
      },
      imap: existingEmailConfig?.imap
        ? {
            enabled: true,
            host: existingEmailConfig.imap.host,
            port: existingEmailConfig.imap.port,
            secure: existingEmailConfig.imap.secure,
            username: existingEmailConfig.imap.username,
            password: "",
            clearPassword: false,
            inboxFolder: existingEmailConfig.imap.inboxFolder || "INBOX",
          }
        : {
            enabled: false,
            host: "",
            port: 993,
            secure: true,
            username: "",
            password: "",
            clearPassword: false,
            inboxFolder: "INBOX",
          },
    },
  });

  const onSubmit = (data: EmailIntegrationFormData) => {
    upsert(buildEmailIntegrationPayload(data), {
      onSuccess: () => {
        toast.success(t("communicationChannels.email.saveSuccess"));
        onClose();
      },
      onError: () => {
        toast.error(t("communicationChannels.email.saveError"));
      },
    });
  };

  const enabled = watch("enabled");
  const smtpSecure = watch("smtp.secure");
  const smtpPassword = watch("smtp.password");
  const smtpPasswordCleared = watch("smtp.clearPassword");
  const imapEnabled = watch("imap.enabled");
  const imapSecure = watch("imap.secure");
  const imapPassword = watch("imap.password");
  const imapPasswordCleared = watch("imap.clearPassword");

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <ToggleRow
        label={t("communicationChannels.email.enabledLabel")}
        help={t("communicationChannels.email.enabledHelp")}
        checked={enabled}
        onCheckedChange={(value) => setValue("enabled", value)}
      />

      <fieldset className="flex flex-col gap-3 rounded-md border p-4">
        <legend className="px-1 text-sm font-semibold">
          {t("communicationChannels.email.smtpHeader")}
        </legend>

        <TextField
          id="email-smtp-host"
          label={t("communicationChannels.email.smtp.host")}
          placeholder="smtp.sendgrid.net"
          error={errors.smtp?.host?.message}
          {...register("smtp.host")}
        />

        <div className="grid grid-cols-2 gap-3">
          <TextField
            id="email-smtp-port"
            type="number"
            label={t("communicationChannels.email.smtp.port")}
            placeholder="587"
            error={errors.smtp?.port?.message}
            {...register("smtp.port", { valueAsNumber: true })}
          />
          <ToggleRow
            compact
            label={t("communicationChannels.email.smtp.secure")}
            help={t("communicationChannels.email.smtp.secureHelp")}
            checked={smtpSecure}
            onCheckedChange={(value) => setValue("smtp.secure", value)}
          />
        </div>

        <TextField
          id="email-smtp-username"
          label={t("communicationChannels.email.smtp.username")}
          placeholder="apikey"
          error={errors.smtp?.username?.message}
          {...register("smtp.username")}
        />

        <SecretField
          id="email-smtp-password"
          label={t("communicationChannels.email.smtp.password")}
          value={smtpPassword}
          cleared={smtpPasswordCleared}
          isFirstSetup={isFirstSetup}
          canClear={canClearSecrets}
          placeholderCreate={t(
            "communicationChannels.email.smtp.passwordPlaceholderCreate",
          )}
          error={errors.smtp?.password?.message}
          onChange={(value) =>
            setValue("smtp.password", value, { shouldValidate: true })
          }
          onToggleClear={(next) =>
            setValue("smtp.clearPassword", next, { shouldValidate: true })
          }
        />

        <TextField
          id="email-smtp-fromAddress"
          type="email"
          label={t("communicationChannels.email.smtp.fromAddress")}
          placeholder="app@guesteria.com"
          error={errors.smtp?.fromAddress?.message}
          {...register("smtp.fromAddress")}
        />
        <TextField
          id="email-smtp-fromName"
          label={t("communicationChannels.email.smtp.fromName")}
          placeholder="Hotel display name"
          error={errors.smtp?.fromName?.message}
          {...register("smtp.fromName")}
        />
        <TextField
          id="email-smtp-replyTo"
          type="email"
          label={t("communicationChannels.email.smtp.replyToAddress")}
          placeholder="hotel-abc@guesteria.com"
          error={errors.smtp?.replyToAddress?.message}
          {...register("smtp.replyToAddress")}
        />
      </fieldset>

      <Accordion type="single" collapsible>
        <AccordionItem value="imap">
          <AccordionTrigger className="text-sm font-semibold">
            {t("communicationChannels.email.imapHeader")}
          </AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col gap-3 pt-1">
              <ToggleRow
                label={t("communicationChannels.email.imap.enabled")}
                help={t("communicationChannels.email.imap.enabledHelp")}
                checked={imapEnabled}
                onCheckedChange={(value) => setValue("imap.enabled", value)}
              />

              {imapEnabled && (
                <>
                  <TextField
                    id="email-imap-host"
                    label={t("communicationChannels.email.imap.host")}
                    placeholder="imap.example.com"
                    error={errors.imap?.host?.message}
                    {...register("imap.host")}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <TextField
                      id="email-imap-port"
                      type="number"
                      label={t("communicationChannels.email.imap.port")}
                      placeholder="993"
                      error={errors.imap?.port?.message}
                      {...register("imap.port", { valueAsNumber: true })}
                    />
                    <ToggleRow
                      compact
                      label={t("communicationChannels.email.imap.secure")}
                      checked={imapSecure}
                      onCheckedChange={(value) =>
                        setValue("imap.secure", value)
                      }
                    />
                  </div>
                  <TextField
                    id="email-imap-username"
                    label={t("communicationChannels.email.imap.username")}
                    placeholder="inbound@guesteria.com"
                    error={errors.imap?.username?.message}
                    {...register("imap.username")}
                  />
                  <SecretField
                    id="email-imap-password"
                    label={t("communicationChannels.email.imap.password")}
                    value={imapPassword}
                    cleared={imapPasswordCleared}
                    isFirstSetup={isFirstSetup}
                    canClear={canClearSecrets}
                    error={errors.imap?.password?.message}
                    onChange={(value) =>
                      setValue("imap.password", value, {
                        shouldValidate: true,
                      })
                    }
                    onToggleClear={(next) =>
                      setValue("imap.clearPassword", next, {
                        shouldValidate: true,
                      })
                    }
                  />
                  <TextField
                    id="email-imap-folder"
                    label={t("communicationChannels.email.imap.inboxFolder")}
                    placeholder="INBOX"
                    error={errors.imap?.inboxFolder?.message}
                    {...register("imap.inboxFolder")}
                  />
                </>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <DialogFooter>
        <Button
          type="button"
          variant="new_outline"
          onClick={onClose}
          disabled={isPending}
        >
          {t("communicationChannels.actions.cancel")}
        </Button>
        <Button type="submit" variant="new_default" disabled={isPending}>
          {isPending
            ? t("communicationChannels.actions.saving")
            : t("communicationChannels.actions.save")}
        </Button>
      </DialogFooter>
    </form>
  );
};

interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string;
}

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ id, label, error, ...props }, ref) => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} ref={ref} {...props} />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  ),
);
TextField.displayName = "TextField";

interface ToggleRowProps {
  label: string;
  help?: string;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  compact?: boolean;
}

const ToggleRow: React.FC<ToggleRowProps> = ({
  label,
  help,
  checked,
  onCheckedChange,
  compact,
}) => (
  <div
    className={`flex items-center justify-between rounded-md border ${
      compact ? "p-2.5" : "p-3"
    }`}
  >
    <div className="flex flex-col">
      <Label>{label}</Label>
      {help && <span className="text-xs text-muted-foreground">{help}</span>}
    </div>
    <Switch
      checked={checked}
      onCheckedChange={onCheckedChange}
      aria-label={label}
    />
  </div>
);
